import { Icon } from '@mdvip/shared/components/blocks/Icon';
import { Alert, AlertDescription, AlertTitle } from '@mdvip/shared/components/ui/alert';
import { Button } from '@mdvip/shared/components/ui/button';
import { Skeleton } from '@mdvip/shared/components/ui/skeleton';
import { useCurrency } from '@mdvip/shared/hooks/useCurrency';
import { useErrorTranslation } from '@mdvip/shared/hooks/useErrorTranslation';
import { useFormatDate } from '@mdvip/shared/hooks/useFormatDate';
import { extractGqlErrorCode } from '@mdvip/shared/lib/gqlErrors';
import { cn } from '@mdvip/shared/lib/utils';
import { useTranslation } from 'react-i18next';
import { useNextPaymentDue } from '@/hooks/payments/nextPaymentDue/useNextPaymentDue';
import { Metric } from './Metric';

interface NextAmountDueMetricProps {
  /** Which of PaymentSummaryCard's two responsive layouts this instance renders. Both mount
   *  simultaneously (CSS visibility, not conditional) — this only changes the markup shape. */
  layout: 'narrow' | 'wide';
  /** Positional padding for the wide 3-column grid, or the narrow column wrapper — owned by the
   *  parent grid, not this metric. */
  className?: string;
}

/**
 * Next Amount Due metric for the "Payment summary" card — the member's next scheduled payment
 * amount and due date. A separate query with its own cache key (not a `useCurrentBalance`
 * selector), so it carries its own loading/error state, independent of the other metrics.
 */
export function NextAmountDueMetric({ layout, className }: NextAmountDueMetricProps) {
  const { t } = useTranslation('members');
  const tError = useErrorTranslation();
  const { data, isLoading, isError, error, refetch } = useNextPaymentDue();
  const { formatCurrency } = useCurrency();
  const { formatDateOnly } = useFormatDate();

  // Discriminated on dueDate, never amount > 0 — a legitimate $0 scheduled payment is a real
  // "due" state, not "nothing scheduled". A non-null dueDate with a null amount (a genuine
  // Jitterbit data anomaly) still renders the due date normally, with just the amount as "—".
  const scheduled = data?.dueDate != null;
  const amountLabel = scheduled ? (data?.amount != null ? formatCurrency(data.amount) : '—') : '—';
  const caption = scheduled
    ? t('payments.paymentSummary.nextAmountDue.due', { date: formatDateOnly(data?.dueDate ?? '') })
    : t('payments.paymentSummary.nextAmountDue.none');
  const colorClass = scheduled ? 'text-foreground' : 'text-muted-foreground';

  const errorBlock = (
    <Alert variant="destructive">
      <Icon name="alertTriangle" weight="solid" />
      {/* The shared Alert's title defaults to a single-line clamp, sized for a full-width banner —
          override it here since this Alert renders inside a much narrower metric column. */}
      <AlertTitle className="line-clamp-none">{t('payments.paymentSummary.nextAmountDue.errorTitle')}</AlertTitle>
      <AlertDescription>
        <p>{tError(extractGqlErrorCode(error))}</p>
        <Button variant="outline" size="sm" className="mt-3" onClick={() => refetch()}>
          <Icon name="refreshCw" size="sm" /> {t('payments.paymentSummary.nextAmountDue.retry')}
        </Button>
      </AlertDescription>
    </Alert>
  );

  if (layout === 'narrow') {
    if (isLoading) {
      return (
        <div className={className} aria-busy="true">
          <Skeleton className="h-3.5 w-24" />
          <Skeleton className="mt-2 h-8 w-20" />
        </div>
      );
    }
    return (
      <div className={className}>
        {isError ? (
          errorBlock
        ) : (
          <>
            <div className="text-sm text-muted-foreground">{t('payments.paymentSummary.nextAmountDue.label')}</div>
            <div className={cn('mt-1 text-[32px] font-bold leading-none tracking-tight tabular-nums', colorClass)}>
              {amountLabel}
            </div>
            <div className="mt-2 text-sm text-muted-foreground">{caption}</div>
          </>
        )}
      </div>
    );
  }

  if (isLoading) {
    return (
      <Metric
        label={<Skeleton className="h-3.5 w-24" />}
        className={className}
        data-slot="metric-next-amount"
        data-state="loading"
        aria-busy="true"
      >
        <Skeleton className="h-8 w-24" />
        <Skeleton className="mt-2 h-3 w-20" />
      </Metric>
    );
  }

  return (
    <Metric
      label={t('payments.paymentSummary.nextAmountDue.label')}
      className={className}
      data-slot="metric-next-amount"
      data-state={isError ? 'error' : scheduled ? 'due' : 'none'}
    >
      {isError ? (
        errorBlock
      ) : (
        <>
          <div className={cn('text-3xl font-semibold leading-none tabular-nums', colorClass)}>{amountLabel}</div>
          <div className="mt-2 text-xs text-muted-foreground">{caption}</div>
        </>
      )}
    </Metric>
  );
}
